import { Signal } from '@angular/core';
import { SortParams } from 'mbs-ui-kit/models/sort';
import { PagedResponse, QueryFiltersParams, QueryPagingParams, QueryParams } from 'mbs-ui-shared@models';
import { Observable } from 'rxjs';

export type DataSliceState = {
  loading: boolean;
  loaded: boolean;
  total: number;
  entityIds: string[];
  isErrorState: boolean;
  obsolescenceMark: number | null;
};

export type PagingSliceState = {
  loading: boolean;
  loaded: boolean;
  total: number;
  currentPageNumber: number;
  perPage: number | null;
  filters: QueryFiltersParams;
  pages: Record<number, string[]>;
  isErrorState: boolean;
  obsolescenceMark: number | null;
  sort: SortParams | null;
};

// eslint-disable-next-line @typescript-eslint/ban-types
export type PagingSignalState<F extends object = Record<string, never>> = {
  data: DataSliceState;
  paging: PagingSliceState;
  loadingById: Record<string, boolean>;
  loadedById: Record<string, boolean>;
  missedIds: string[];
  custom: F;
};

export const initialDataState: DataSliceState = {
  loading: false,
  loaded: false,
  total: 0,
  entityIds: [],
  isErrorState: false,
  obsolescenceMark: null
};

export const initialPagingSliceState: PagingSliceState = {
  loading: false,
  loaded: false,
  total: 0,
  currentPageNumber: 1,
  perPage: null,
  filters: {},
  pages: {},
  isErrorState: false,
  obsolescenceMark: null,
  sort: null
};

// eslint-disable-next-line @typescript-eslint/ban-types
export function createInitialPagingSignalState<F extends object = Record<string, never>>(customInitial?: F): PagingSignalState<F> {
  return {
    data: { ...initialDataState },
    paging: { ...initialPagingSliceState },
    loadingById: {},
    loadedById: {},
    missedIds: [],
    custom: (customInitial ?? {}) as F
  };
}

// eslint-disable-next-line @typescript-eslint/ban-types
export type WithPagingAndDataConfig<T extends object, F extends object = Record<string, never>> = {
  selectId: (entity: T) => string;
  getEntityById?: () => ((id: string) => Observable<T>) | null;
  getEntitiesByIds?: () => ((ids: string[]) => Observable<T[]>) | null;
  getEntities: () => (params: QueryFiltersParams & QueryPagingParams) => Observable<PagedResponse<T>>;
  // eslint-disable-next-line @typescript-eslint/ban-types
  initialCustomState?: F;
  // eslint-disable-next-line @typescript-eslint/ban-types
  onPageLoaded?: (result: PagedResponse<T>, patchCustom: (updater: (custom: F) => F) => void) => void;
};

/**
 * Minimal interface describing what withPagingAndData contributes to a signal store.
 * Used by PagingStoreFacade to type the injected store without coupling to a concrete type.
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export interface PagingAndDataStoreInterface<T extends object, F extends object = Record<string, never>> {
  // Raw state signals (from withState / withEntities)
  readonly entityMap: Signal<Record<string, T>>;
  readonly ids: Signal<(string | number)[]>;
  readonly entities: Signal<T[]>;
  readonly data: Signal<DataSliceState>;
  readonly paging: Signal<PagingSliceState>;
  readonly loadingById: Signal<Record<string, boolean>>;
  readonly loadedById: Signal<Record<string, boolean>>;
  readonly missedIds: Signal<string[]>;
  readonly custom: Signal<F>;

  // Computed signals
  readonly selectAll: Signal<T[]>;
  readonly selectFirstEntity: Signal<T | undefined>;
  readonly selectLoading: Signal<boolean>;
  readonly selectDataLoading: Signal<boolean>;
  readonly selectDataLoaded: Signal<boolean>;
  readonly selectDataTotal: Signal<number>;
  readonly selectData: Signal<T[]>;
  readonly selectDataCount: Signal<number>;
  readonly selectDataHasError: Signal<boolean>;
  readonly selectAllDataLoaded: Signal<boolean>;
  readonly selectPagingLoading: Signal<boolean>;
  readonly selectPagingLoaded: Signal<boolean>;
  readonly selectPagingCurrentPageNumber: Signal<number>;
  readonly selectPagingPerPage: Signal<number | null>;
  readonly selectPagingTotal: Signal<number>;
  readonly selectPagingFilters: Signal<QueryFiltersParams>;
  readonly selectPagingHasError: Signal<boolean>;
  readonly selectPagingPageNumbers: Signal<number[]>;
  readonly selectCurrentPage: Signal<T[]>;
  readonly selectDataEntityLoading: Signal<boolean>;
  readonly selectPagingEntityLoading: Signal<boolean>;
  readonly selectSortSettings: Signal<SortParams | null>;

  // Signal-returning methods (factory computed)
  entityById(id: string): Signal<T | undefined>;
  entitiesById(ids: string[]): Signal<T[]>;
  loadedEntityById(id: string): Signal<T | undefined>;
  loadedEntitiesById(ids: string[]): Signal<T[]>;
  loadingForId(id: string): Signal<boolean>;
  missedById(id: string): Signal<boolean>;
  missedByIds(ids: string[]): Signal<string[]>;

  // Identity helper
  entityId(entity: T): string;

  // Synchronous commands
  setEntities(entities: T[]): void;
  clearEntities(): void;
  clearData(): void;
  clearPages(): void;
  clearAll(): void;

  // Async rxMethods (called like functions; internally manage their own subscriptions)
  loadEntityById(params: { id: string; force?: boolean; quiet?: boolean }): void;
  loadEntitiesByIds(params: { ids: string[]; force?: boolean; quiet?: boolean }): void;
  loadData(params?: { isFullRefresh?: boolean; allowLoadMore?: boolean; limit?: number } | undefined): void;
  loadPage(params: { filters?: QueryParams; pageNumber?: number; perPage?: number; isFullRefresh?: boolean }): void;
  setPageNumber(pageNumber: number): void;
  setPerPage(perPage: number): void;
  setSortSettings(sort: SortParams): void;
  refreshDataAndPaging(): void;

  // Imperative fetch + store update (returns Observable for caller to subscribe)
  directGetEntityById?(id: string): Observable<T> | null;
  directGetEntitiesByIds?(ids: string[]): Observable<T[]> | null;
}

// eslint-disable-next-line @typescript-eslint/ban-types
export enum PagingActionTypes {
  SetEntities = 'Set Entities',
  Clear = 'Clear Entities',
  LoadEntityById = 'Load Entity',
  LoadEntityByIdSuccess = 'Load Entity Success',
  LoadEntityByIdError = 'Load Entity Error',
  LoadEntitiesByIds = 'Load Entities',
  LoadEntitiesByIdsSuccess = 'Load Entities Success',
  LoadEntitiesByIdsError = 'Load Entities Error',
  SetLoading = 'Set Loading',
  // data
  LoadData = 'Load Data',
  LoadDataSuccess = 'Load Data Success',
  LoadDataError = 'Load Data Error',
  ClearData = 'Clear Data',
  // paging
  LoadPage = 'Load Page',
  LoadPageSuccess = 'Load Page Success',
  LoadPageError = 'Load Page Error',
  ClearPages = 'Clear Pages',
  SetPageNumber = 'Set Page Number',
  SetPageNumberSuccess = 'Set Page Number Success',
  SetPerPage = 'Set Per Page',
  RefreshDataAndPaging = 'Refresh Data And Paging',
  RefreshDataAndPagingSuccess = 'Refresh Data And Paging Success'
}
